/**
 * Mailings API — contract line #32 (`/api/mailings…`, ADDITIVE v1.9.26, T.Z124).
 *
 *   #32.1 GET  /api/mailings                                    → `{ data, meta, summary }`
 *   #32.2 GET  /api/mailings/run-status                         → `{ data:{running, run, ledger} }`
 *   #32.3 GET  /api/mailings/delivery-capabilities              → `{ data:{configured, …} }`
 *   #32.4 GET  /api/mailings/recipients/:applicationId/preview  → `{ data:{html, history} }`
 *   #32.5 POST /api/mailings/test-send                          → `{ data:{sent:true} }`
 *   #32.6 POST /api/mailings/send-all                           → 202 `{ data:{queued:true, runId} }`
 *   #32.7 POST /api/mailings/delivery-recheck                   → `{ data:{checked, delivered, …} }`
 *
 * The shapes mirror `apps/api/src/mailing/mailing.service.ts` 1:1 (the generated
 * `api-types.ts` is not reachable from the bundle — `@cb-manager/shared` exports
 * only its barrel), the way `quotas.ts` mirrors #12–#15.
 *
 * Error semantics the page surfaces verbatim:
 *   everything below MANAGER    → 403 FORBIDDEN (the nav hides the item instead)
 *   no Postbox credentials      → 503 MAILING_NOT_CONFIGURED (the kind stand)
 *   the transport refused       → 502 MAILING_SEND_FAILED (its own words, verbatim)
 *   send-all while running      → 409 MAILING_RUN_IN_PROGRESS
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, apiRequest, buildQuery, requestEnvelope, type Envelope } from './client';

export const MAILINGS_ROOT = '/mailings';
export const MAILING_TYPE_DOCUMENTS_UNSIGNED = 'DOCUMENTS_UNSIGNED';

export type MailingBucketWire = 'NOT_ARRIVED' | 'NOT_SIGNED';
export type MailingTrack = 'AI' | 'PYTHON';
export type MailingDeliveryStatus = 'ACCEPTED' | 'DELIVERED' | 'FAILED' | 'SKIPPED';
export type MailingDeliveryFilter = 'not_sent' | 'accepted' | 'delivered' | 'failed';
/** T.Z146 (ADDITIVE v1.9.39) — the post-send outcome the owner asked to see. */
export type MailingOutcomeFilter = 'signed-after' | 'declined-after';
export type MailingOutcome = MailingOutcomeFilter | null;

/** The #32.1 row — the mirror of `MailingRecipientDto`. */
export interface MailingRecipientDto {
  applicationId: number;
  pguId: string | null;
  fullName: string | null;
  email: string | null;
  programName: string | null;
  bucket: MailingBucketWire;
  track: MailingTrack;
  trackResolved: boolean;
  greetingName: string | null;
  renderReady: boolean;
  sentCount: number;
  lastSentAt: string | null;
  lastDeliveryStatus: MailingDeliveryStatus | null;
  lastStatusAtSend: string | null;
  /** ADDITIVE v1.9.39 — `signed-after` / `declined-after`, null = no outcome yet. */
  outcome?: MailingOutcome;
}

/** One ledger row — the mirror of `MailingMessageDto`. `bucket` is nullable
 * since contract v1.9.30: a mailing LIST's row and the SECOND fixed type's row
 * (v1.9.34, T.Z140) carry no contract bucket. */
export interface MailingMessageDto {
  id: number;
  mailingType: string;
  bucket: MailingBucketWire | null;
  statusAtSend: string;
  email: string | null;
  track: MailingTrack;
  trackResolved: boolean;
  deliveryStatus: MailingDeliveryStatus;
  deliveryCheckedAt: string | null;
  error: string | null;
  runId: string | null;
  sesMessageId: string | null;
  sentAt: string | null;
}

export interface MailingSummaryDto {
  buckets: { notArrived: number; notSigned: number; total: number };
  delivery: { notSent: number; accepted: number; delivered: number; failed: number };
  tracks: { ai: number; python: number; unresolved: number };
  /** ADDITIVE v1.9.39 (T.Z146) — the post-send outcome counts over the campaign's
   * journal. Optional so an older stub/answer renders the cards as zeros. */
  outcomes?: { signedAfterSend: number; declinedAfterSend: number };
}

export interface MailingsListDto {
  data: MailingRecipientDto[];
  meta: { page: number; pageSize: number; total: number };
  summary: MailingSummaryDto;
}

export interface MailingsRunProgress {
  runId: string;
  startedAt: string;
  finishedAt: string | null;
  bucket: MailingBucketWire | 'ALL';
  total: number;
  processed: number;
  accepted: number;
  failed: number;
  skipped: number;
}

export interface MailingsRunStatusDto {
  running: boolean;
  run: MailingsRunProgress | null;
  ledger: { accepted: number; delivered: number; failed: number; skipped: number };
}

export interface MailingsCapabilitiesDto {
  configured: boolean;
  /** The sender the LETTERS carry — MAILING_FROM_EMAIL, default support_cf@ismart.org. */
  fromEmail: string;
  identities: { ok: true; names: string[] } | { ok: false; error: string };
  configurationSets: { ok: true; names: string[] } | { ok: false; error: string };
  configurationSetInUse: string | null;
  sendPerSecond: number;
}

export interface MailingPreviewDto {
  applicationId: number;
  subject: string;
  html: string;
  startUrl: string;
  greetingName: string | null;
  track: MailingTrack;
  recipient: MailingRecipientDto;
  history: MailingMessageDto[];
}

export interface MailingsListParams {
  page?: number;
  pageSize?: number;
  bucket?: MailingBucketWire;
  delivery?: MailingDeliveryFilter;
  /** ADDITIVE v1.9.39 (T.Z146) — the post-send outcome filter; its universe is
   * the journal, so `bucket` does not AND with it on the wire. */
  outcome?: MailingOutcomeFilter;
}

/** #32.1 — the list + summary. ONE raw read: the envelope here carries `summary`
 * alongside `data`/`meta`, so `requestEnvelope` (which keeps only the payload
 * key) would drop it. */
export function fetchMailingsList(params: MailingsListParams, signal?: AbortSignal): Promise<MailingsListDto> {
  const query = buildQuery({
    ...(params.bucket ? { bucket: params.bucket } : {}),
    ...(params.delivery ? { delivery: params.delivery } : {}),
    ...(params.outcome ? { outcome: params.outcome } : {}),
    ...(params.page ? { page: String(params.page) } : {}),
    ...(params.pageSize ? { pageSize: String(params.pageSize) } : {}),
  });
  return apiRequest<MailingsListDto>('GET', `${MAILINGS_ROOT}${query}`, { signal });
}

/** #32.2 — the run status. The query polls ITSELF while a run is draining (the
 * interval lives inside the query state, so no caller has to know), and stops
 * the moment the run finishes. */
export function useMailingsRunStatus() {
  return useQuery({
    queryKey: ['mailings', 'run-status'],
    queryFn: ({ signal }) => requestEnvelope<MailingsRunStatusDto>('GET', `${MAILINGS_ROOT}/run-status`, ['data'], { signal }),
    refetchInterval: (query) => (query.state.data?.data.running ? 2000 : false),
  });
}

/** #32.3 — the SES capability probe (read-only, re-read on demand). */
export function useMailingsCapabilities() {
  return useQuery({
    queryKey: ['mailings', 'capabilities'],
    queryFn: ({ signal }) => requestEnvelope<MailingsCapabilitiesDto>('GET', `${MAILINGS_ROOT}/delivery-capabilities`, ['data'], { signal }),
  });
}

/** #32.4 — the rendered letter of one recipient + his history. */
export function useMailingPreview(applicationId: number | null) {
  return useQuery({
    queryKey: ['mailings', 'preview', applicationId],
    enabled: applicationId !== null,
    queryFn: ({ signal }) =>
      requestEnvelope<MailingPreviewDto>(
        'GET',
        `${MAILINGS_ROOT}/recipients/${applicationId}/preview`,
        ['data'],
        { signal },
      ),
  });
}

export class MailingMutationError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'MailingMutationError';
  }
}

async function post<T>(path: string, body: unknown): Promise<Envelope<T>> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // Non-JSON — the status-derived code is the honest fallback.
    }
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new MailingMutationError(
      response.status,
      error?.code ?? `HTTP_${response.status}`,
      error?.message ?? response.statusText,
    );
  }
  return (await response.json()) as Envelope<T>;
}

/** #32.5 — one test letter. */
export function useMailingTestSend() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { applicationId: number; email: string; name?: string }) =>
      post<{ sent: boolean; to: string; applicationId: number; startUrl: string }>(`${MAILINGS_ROOT}/test-send`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings'] }),
  });
}

/** #32.6 — arm the paced send-all run (202: armed, not done). */
export function useMailingSendAll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { bucket?: MailingBucketWire }) =>
      post<{ queued: boolean; runId: string; total: number; skippedAlreadySent: number }>(`${MAILINGS_ROOT}/send-all`, input ?? {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings'] }),
  });
}

/** #32.7 — the delivery read-back over the newest accepted letters. */
export function useMailingRecheck() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ checked: number; delivered: number; unchanged: number; errors: string[] }>(`${MAILINGS_ROOT}/delivery-recheck`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings'] }),
  });
}

// ───────────────────────────────────────────────────────────────────────────────
// The SECOND fixed mailing (ADDITIVE v1.9.34, T.Z140): «Рассылка получившим
// ссылку» over `status=LINK_RECEIVED`, under `/mailings/link-received…`. The
// mirror of `apps/api/src/mailing/mailing-link-received.service.ts`, the way the
// block above mirrors the first type — with the PER-RECIPIENT link where #32
// carries only the track, and readiness that includes it.

export const MAILINGS_LINK_ROOT = '/mailings/link-received';
/** The wire value of the second fixed type (the select's option value). */
export const MAILING_TYPE_EVI_NOT_FINISHED = 'EVI_NOT_FINISHED';

export type MailingLinkAudience = '8_9' | '10_11' | 'college';

/** The #32.8 row — the mirror of `MailingLinkRecipientDto`. */
export interface MailingLinkRecipientDto {
  applicationId: number;
  pguId: string | null;
  fullName: string | null;
  email: string | null;
  programName: string | null;
  classCourse: string | null;
  track: MailingTrack;
  trackResolved: boolean;
  audience: MailingLinkAudience | null;
  startUrl: string | null;
  greetingName: string | null;
  renderReady: boolean;
  sentCount: number;
  lastSentAt: string | null;
  lastDeliveryStatus: MailingDeliveryStatus | null;
  lastStatusAtSend: string | null;
}

export interface MailingLinkSummaryDto {
  total: number;
  ready: number;
  notReady: number;
  delivery: { notSent: number; accepted: number; delivered: number; failed: number };
  tracks: { ai: number; python: number; unresolved: number };
}

export interface MailingLinkListDto {
  data: MailingLinkRecipientDto[];
  meta: { page: number; pageSize: number; total: number };
  summary: MailingLinkSummaryDto;
}

export interface MailingLinkRunProgress {
  runId: string;
  startedAt: string;
  finishedAt: string | null;
  total: number;
  processed: number;
  accepted: number;
  failed: number;
  skipped: number;
}

export interface MailingLinkRunStatusDto {
  running: boolean;
  run: MailingLinkRunProgress | null;
  ledger: { accepted: number; delivered: number; failed: number; skipped: number };
}

export interface MailingLinkPreviewDto {
  applicationId: number;
  subject: string;
  html: string;
  startUrl: string;
  audience: MailingLinkAudience | null;
  greetingName: string | null;
  track: MailingTrack;
  recipient: MailingLinkRecipientDto;
  history: MailingMessageDto[];
}

export interface MailingLinkListParams {
  page?: number;
  pageSize?: number;
  delivery?: MailingDeliveryFilter;
}

/** #32.8 — the list + summary (the same one-read envelope shape #32.1 has). */
export function fetchMailingsLinkList(params: MailingLinkListParams, signal?: AbortSignal): Promise<MailingLinkListDto> {
  const query = buildQuery({
    ...(params.delivery ? { delivery: params.delivery } : {}),
    ...(params.page ? { page: String(params.page) } : {}),
    ...(params.pageSize ? { pageSize: String(params.pageSize) } : {}),
  });
  return apiRequest<MailingLinkListDto>('GET', `${MAILINGS_LINK_ROOT}${query}`, { signal });
}

/** #32.9 — the run status of the second type (same polling contract as #32.2). */
export function useMailingsLinkRunStatus() {
  return useQuery({
    queryKey: ['mailings-link', 'run-status'],
    queryFn: ({ signal }) =>
      requestEnvelope<MailingLinkRunStatusDto>('GET', `${MAILINGS_LINK_ROOT}/run-status`, ['data'], { signal }),
    refetchInterval: (query) => (query.state.data?.data.running ? 2000 : false),
  });
}

/** #32.10 — the rendered letter of one recipient + his history. */
export function useMailingLinkPreview(applicationId: number | null) {
  return useQuery({
    queryKey: ['mailings-link', 'preview', applicationId],
    enabled: applicationId !== null,
    queryFn: ({ signal }) =>
      requestEnvelope<MailingLinkPreviewDto>(
        'GET',
        `${MAILINGS_LINK_ROOT}/recipients/${applicationId}/preview`,
        ['data'],
        { signal },
      ),
  });
}

/** #32.11 — one test letter of the second type. */
export function useMailingLinkTestSend() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { applicationId: number; email: string; name?: string }) =>
      post<{ sent: boolean; to: string; applicationId: number; startUrl: string }>(`${MAILINGS_LINK_ROOT}/test-send`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings-link'] }),
  });
}

/** #32.12 — arm the paced send-all run of the second type (409 while ANY campaign drains). */
export function useMailingLinkSendAll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post<{ queued: boolean; runId: string; total: number; skippedAlreadySent: number }>(`${MAILINGS_LINK_ROOT}/send-all`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings-link'] }),
  });
}

/** #32.13 — the delivery read-back of the second type. */
export function useMailingLinkRecheck() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post<{ checked: number; delivered: number; unchanged: number; errors: string[] }>(`${MAILINGS_LINK_ROOT}/delivery-recheck`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings-link'] }),
  });
}

// ───────────────────────────────────────────────────────────────────────────────
// The THIRD fixed mailing (ADDITIVE v1.9.55, T.Z182): «Ошибка подписи договора:
// не указано согласие родителей для заявителей от 14 до 18 лет» — the error
// letter over the register's five-axis page
// (14–17, contract signed, no consent tick, self-signed, not enrolled), under
// `/mailings/consent-unsigned-14-18…`. The mirror of
// `apps/api/src/mailing/mailing-consent-unsigned.service.ts`, the way the block
// above mirrors the second type — with the T.Z124 track welcome page as the
// letter's link (never null) and readiness that is name + e-mail only.

export const MAILINGS_CONSENT_ROOT = '/mailings/consent-unsigned-14-18';
/** The wire value of the third fixed type (the select's option value). */
export const MAILING_TYPE_CONSENT_UNSIGNED = 'CONSENT_ERROR_UNSIGNED_14_18';

/** The #32.14 row — the mirror of `MailingConsentRecipientDto`. */
export interface MailingConsentRecipientDto {
  applicationId: number;
  pguId: string | null;
  fullName: string | null;
  email: string | null;
  programName: string | null;
  track: MailingTrack;
  trackResolved: boolean;
  /** The «личный кабинет» link — the track's welcome page, never null. */
  startUrl: string;
  /** ADDITIVE v1.9.57 (T.Z185) — the ЕПГУ course page of THIS child's programme
   * × audience; null = unresolved, and a null here makes the row NOT renderReady. */
  gosuslugiTestLink: string | null;
  greetingName: string | null;
  renderReady: boolean;
  sentCount: number;
  lastSentAt: string | null;
  lastDeliveryStatus: MailingDeliveryStatus | null;
  lastStatusAtSend: string | null;
}

export interface MailingConsentSummaryDto {
  total: number;
  ready: number;
  notReady: number;
  delivery: { notSent: number; accepted: number; delivered: number; failed: number };
  tracks: { ai: number; python: number; unresolved: number };
}

export interface MailingConsentListDto {
  data: MailingConsentRecipientDto[];
  meta: { page: number; pageSize: number; total: number };
  summary: MailingConsentSummaryDto;
}

export interface MailingConsentRunProgress {
  runId: string;
  startedAt: string;
  finishedAt: string | null;
  total: number;
  processed: number;
  accepted: number;
  failed: number;
  skipped: number;
}

export interface MailingConsentRunStatusDto {
  running: boolean;
  run: MailingConsentRunProgress | null;
  ledger: { accepted: number; delivered: number; failed: number; skipped: number };
}

export interface MailingConsentPreviewDto {
  applicationId: number;
  subject: string;
  html: string;
  startUrl: string;
  /** ADDITIVE v1.9.57 (T.Z185) — the ЕПГУ course page the letter's course link points at. */
  gosuslugiTestLink: string | null;
  greetingName: string | null;
  track: MailingTrack;
  recipient: MailingConsentRecipientDto;
  history: MailingMessageDto[];
}

export interface MailingConsentListParams {
  page?: number;
  pageSize?: number;
  delivery?: MailingDeliveryFilter;
}

/** #32.14 — the list + summary (the same one-read envelope shape #32.1 has). */
export function fetchMailingsConsentList(params: MailingConsentListParams, signal?: AbortSignal): Promise<MailingConsentListDto> {
  const query = buildQuery({
    ...(params.delivery ? { delivery: params.delivery } : {}),
    ...(params.page ? { page: String(params.page) } : {}),
    ...(params.pageSize ? { pageSize: String(params.pageSize) } : {}),
  });
  return apiRequest<MailingConsentListDto>('GET', `${MAILINGS_CONSENT_ROOT}${query}`, { signal });
}

/** #32.15 — the run status of the third type (same polling contract as #32.2). */
export function useMailingsConsentRunStatus() {
  return useQuery({
    queryKey: ['mailings-consent', 'run-status'],
    queryFn: ({ signal }) =>
      requestEnvelope<MailingConsentRunStatusDto>('GET', `${MAILINGS_CONSENT_ROOT}/run-status`, ['data'], { signal }),
    refetchInterval: (query) => (query.state.data?.data.running ? 2000 : false),
  });
}

/** #32.16 — the rendered letter of one recipient + his history. */
export function useMailingConsentPreview(applicationId: number | null) {
  return useQuery({
    queryKey: ['mailings-consent', 'preview', applicationId],
    enabled: applicationId !== null,
    queryFn: ({ signal }) =>
      requestEnvelope<MailingConsentPreviewDto>(
        'GET',
        `${MAILINGS_CONSENT_ROOT}/recipients/${applicationId}/preview`,
        ['data'],
        { signal },
      ),
  });
}

/** #32.17 — one test letter of the third type. */
export function useMailingConsentTestSend() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { applicationId: number; email: string; name?: string }) =>
      post<{ sent: boolean; to: string; applicationId: number; startUrl: string; gosuslugiTestLink: string | null }>(
        `${MAILINGS_CONSENT_ROOT}/test-send`,
        input,
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings-consent'] }),
  });
}

/** #32.18 — arm the paced send-all run of the third type (409 while ANY campaign drains). */
export function useMailingConsentSendAll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post<{ queued: boolean; runId: string; total: number; skippedAlreadySent: number }>(`${MAILINGS_CONSENT_ROOT}/send-all`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings-consent'] }),
  });
}

/** #32.19 — the delivery read-back of the third type. */
export function useMailingConsentRecheck() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post<{ checked: number; delivered: number; unchanged: number; errors: string[] }>(`${MAILINGS_CONSENT_ROOT}/delivery-recheck`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailings-consent'] }),
  });
}
